import { createTranslator, type Locale, type Translator } from '@telestore/i18n';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, restoreSession, setApiLocale, setShopId, setToken, type SessionPayload } from './api.js';
import { initialLocale } from './locale.js';

/**
 * Admin session and shop selection.
 *
 * Both sign-in doors from §3 land here: the Telegram Login Widget on the web,
 * and `initData` when the admin runs as a Mini App. There is no password path
 * because there are no passwords.
 */

interface SessionContextValue {
  user: SessionPayload['user'] | null;
  memberships: SessionPayload['memberships'];
  shopId: string | null;
  selectShop(id: string): void;
  signOut(): void;
  locale: Locale;
  setLocale(locale: Locale): void;
  t: Translator;
  status: 'loading' | 'anonymous' | 'ready';
  error: string | null;
  signInDev(): Promise<void>;
  refresh(): Promise<void>;
  /**
   * Take a session the Login Widget just produced.
   *
   * Exposed because the widget lives outside this provider: it hands back a
   * session and something has to store the token. The sign-in screen used to
   * call `window.location.reload()` instead, which threw the session away —
   * `signInWithWidget` only returns it, it does not persist anything — so the
   * reload found no token and rendered the sign-in screen again. Telegram said
   * the login worked, and nothing happened.
   */
  adoptSession(session: SessionPayload): void;
  /**
   * True when the admin is running inside Telegram.
   *
   * Identity is already settled there — `initData` names the user — so the
   * Login Widget has nothing to add, and offering it is worse than useless: its
   * flow runs through `oauth.telegram.org` inside a Telegram webview, which is
   * the one place it cannot finish.
   */
  isMiniApp: boolean;
  /** Retry the `initData` sign-in. The only sign-in that belongs in a Mini App. */
  signInMiniApp(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

const LOCALE_KEY = 'telestore.admin.locale';

interface TelegramWebApp {
  initData: string;
  /** Unsigned copy of the same payload — fine for a language hint, never for auth. */
  initDataUnsafe?: { user?: { language_code?: string } };
  ready(): void;
  expand(): void;
}

function webApp(): TelegramWebApp | null {
  const app = (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
  return app && app.initData.length > 0 ? app : null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionPayload['user'] | null>(null);
  const [memberships, setMemberships] = useState<SessionPayload['memberships']>([]);
  const [shopId, setShopIdState] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'anonymous' | 'ready'>('loading');
  const [error, setError] = useState<string | null>(null);

  const [locale, setLocaleState] = useState<Locale>(() =>
    initialLocale(
      typeof location !== 'undefined' ? location.search : '',
      typeof localStorage !== 'undefined' ? localStorage.getItem(LOCALE_KEY) : null,
      webApp()?.initDataUnsafe?.user?.language_code ?? null,
    ),
  );

  useEffect(() => {
    setApiLocale(locale);
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(LOCALE_KEY, locale);
    } catch {
      /* non-fatal */
    }
  }, [locale]);

  const adopt = useCallback((session: SessionPayload) => {
    setToken(session.token);
    setUser(session.user);
    setMemberships(session.memberships);

    const stored = restoreSession().shopId;
    const next =
      session.memberships.find((m) => m.shopId === stored)?.shopId ??
      session.memberships[0]?.shopId ??
      null;
    setShopId(next);
    setShopIdState(next);
    setStatus('ready');
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me.user);
      setMemberships(me.memberships);
      setStatus('ready');
    } catch {
      setStatus('anonymous');
    }
  }, []);

  const signInMiniApp = useCallback(async () => {
    const app = webApp();
    if (!app) return;
    try {
      adopt(await api.signInWithInitData(app.initData));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : null);
      setStatus('anonymous');
    }
  }, [adopt]);

  const signInDev = useCallback(async () => {
    try {
      adopt(await api.devSignIn());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sign-in failed');
      setStatus('anonymous');
    }
  }, [adopt]);

  // Bootstrap: Mini App first, then a stored token, then anonymous.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const app = webApp();
      if (app) {
        app.ready();
        app.expand();
        try {
          const session = await api.signInWithInitData(app.initData);
          if (!cancelled) adopt(session);
          return;
        } catch (cause) {
          if (!cancelled) setError(cause instanceof Error ? cause.message : null);
        }
      }

      const stored = restoreSession();
      if (stored.token) {
        try {
          const me = await api.me();
          if (cancelled) return;
          setUser(me.user);
          setMemberships(me.memberships);
          const next =
            me.memberships.find((m) => m.shopId === stored.shopId)?.shopId ??
            me.memberships[0]?.shopId ??
            null;
          setShopId(next);
          setShopIdState(next);
          setStatus('ready');
          return;
        } catch {
          setToken(null);
        }
      }

      if (!cancelled) setStatus('anonymous');
    })();

    return () => {
      cancelled = true;
    };
  }, [adopt]);

  const value = useMemo<SessionContextValue>(
    () => ({
      user,
      memberships,
      shopId,
      selectShop: (id: string) => {
        setShopId(id);
        setShopIdState(id);
      },
      signOut: () => {
        setToken(null);
        setShopId(null);
        setUser(null);
        setMemberships([]);
        setShopIdState(null);
        setStatus('anonymous');
      },
      locale,
      setLocale: setLocaleState,
      t: createTranslator(locale),
      status,
      error,
      signInDev,
      refresh,
      adoptSession: adopt,
      isMiniApp: webApp() !== null,
      signInMiniApp,
    }),
    [user, memberships, shopId, locale, status, error, signInDev, refresh, adopt, signInMiniApp],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Telegram Login Widget. It renders an iframe that calls a global callback, so
 * the component installs one and cleans it up on unmount.
 */
export function TelegramLoginButton({
  botUsername,
  onSession,
}: {
  botUsername: string;
  onSession(session: SessionPayload): void;
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = document.getElementById('tg-login');
    if (!container) return;

    (window as unknown as { onTelegramAuth?: (user: Record<string, unknown>) => void }).onTelegramAuth =
      (payload) => {
        void api
          .signInWithWidget(payload)
          .then(onSession)
          .catch(() => setFailed(true));
      };

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '9');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    container.appendChild(script);

    return () => {
      container.innerHTML = '';
      delete (window as unknown as { onTelegramAuth?: unknown }).onTelegramAuth;
    };
  }, [botUsername, onSession]);

  return (
    <div>
      <div id="tg-login" />
      {failed ? (
        <p className="mt-2 text-sm" style={{ color: 'var(--color-danger)' }}>
          Telegram sign-in failed. Please try again.
        </p>
      ) : null}
    </div>
  );
}
